import API from '../../../APIs/API';
import Utils from '../../../Core/Utils';
import {AgeComputedRounded} from '../../../Core/helpers/computed/Age';
import {ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/QuoteXref';
import {quoteGuards} from '../../../Core/mobx/ParameterStore/QuoteGuards';
import {fieldApi} from '../../../Core/mobx/ParameterStore/QuoteXref';
import {TemplateDataFields} from '../../ContactForm/EmailEditor/EmailEditor';
import {filterQuoteResponse} from './helpers';

export const ixnAPI = async (
    parameterStore: ParameterStore,
    ptype: 'Term' | 'GUL',
    formType?: 'single-quote' | 'combination-quote',
) => {
    let quote = parameterStore.quote;
    if (formType === 'combination-quote' && parameterStore.quoteCombination) {
        quote = parameterStore.quoteCombination;
    }

    let args = new Array<{name: string; value: any}>();

    const gender = parameterStore.approval.gender.value;
    args.push({name: 'gender', value: gender});

    const age = parameterStore.approval.age.value;
    args.push({name: 'current_age', value: age});
    const bday = parameterStore.approval.bday.value;
    const bmonth = parameterStore.approval.bmonth.value;
    const byear = parameterStore.approval.byear.value;
    const child_rider = quote.childrensRiderTermSelector.value;
    const productSelected = quote.productTerm.value;
    if (ptype == 'Term' && child_rider && productSelected != 'NationalLifeGroupLSWTerm') {
        const child_rider_units = quote.childRiderUnitsTerm.value;
        args.push({name: 'child_rider_units', value: child_rider_units});
    }
    // ideally, we should calculate nearest age based on the insurance company. However, currently, everything under term/GUL uses nearest age, not actuall age
    // so this works for now. When refactoring, wwe should build a service that calculate it based on insurrance company
    const nearestAge = AgeComputedRounded(+bday, +bmonth, +byear);
    args.push({name: 'nearest_age', value: nearestAge});

    const state = parameterStore.approval.state.value;
    args.push({name: 'state', value: state});

    const qargs = Object.entries(quote)
        .map(([n, p]) => {
            if (quoteGuards.hasOwnProperty(n) && !quoteGuards[n](parameterStore, quote)) {
                return;
            }

            const field = fieldApi.hasOwnProperty(n) ? fieldApi[n as keyof typeof fieldApi] : null;

            if (field && 'api' in field && field.api != null) {
                const a = (field.api as Array<{api: string; name: string; value?: any}>).find(
                    (a: any) => a.api == 'ixn',
                );
                if (a == null) {
                    return;
                }

                const ret: {name: string; value: any} = {name: a.name, value: null};
                ret.value = p.value;
                const fieldOption: {options: string[]; optionValues?: string[]} | null = fieldOptions.hasOwnProperty(n)
                    ? fieldOptions[n as keyof typeof fieldOptions]
                    : null;
                if (fieldOption != null) {
                    const index = fieldOption.options.findIndex((o) => Utils.MakeVarName(o) == ret.value);
                    const options = fieldOption.optionValues ? fieldOption.optionValues : fieldOption.options;
                    if (index > -1) {
                        ret.value = options[index];
                    } else {
                        console.log('could not find index', ret.value, fieldOption.options);
                    }
                }

                if (field.type == 'number' || field.type == 'currency') {
                    if (ret.value == null) ret.value = 0;
                    ret.value = +ret.value;
                }

                if (field.type == 'boolean') {
                    if (ret.value == null) p.value = false;
                    ret.value = p.value;
                }
                return ret;
            }
        })
        .filter((x) => x != null) as Array<{name: string; value: any}>;

    args = args.concat(qargs);

    const tobacco = args.find((x) => x.name == 'tobacco');
    if (tobacco != null) {
        tobacco.value = tobacco.value.toLowerCase().trim() == 'tobacco';
    }

    const resp = await API.Call<{errors?: object[]} & Record<string, string>[]>('ixn/v1/quotes', 'POST', {
        method: 'GET',
        data: args,
    });

    if (resp?.errors != null && resp.errors.length > 0) {
        return {error: resp.errors.map((x: any) => x.title).join('. ')};
    }

    const outParams: Array<{
        id: string;
        label: string;
        type: 'year' | 'currency' | 'string';
    }> = [
        {id: 'carrier_name', label: 'Carrier', type: 'string'},
        {id: 'product_name', label: 'Product name', type: 'string'},
        {id: 'product_type', label: 'Product type', type: 'string'},
        {id: 'carrier_health_category', label: 'Carrier Health Category', type: 'string'},
        {id: 'ixn_health_category', label: 'IXN Health Category', type: 'string'},
        {id: 'face_amount', label: 'Face amount', type: 'currency'},
        {id: 'premium_monthly', label: 'Monthly premium', type: 'currency'},
        {id: 'premium_quarterly', label: 'Quarterly premium', type: 'currency'},
        {id: 'premium_semi_annual', label: 'Semi-annual premium', type: 'currency'},
        {id: 'premium_annual', label: 'Annual premium', type: 'currency'},
        {id: 'child_rider_monthly', label: 'Child Rider Monthly Premium', type: 'currency'},
        {id: 'child_rider_quarterly', label: 'Child Rider Quarterly Premium', type: 'currency'},
        {id: 'child_rider_semi_annual', label: 'Child Rider Semi-Annual Premium', type: 'currency'},
        {id: 'child_rider_annual', label: 'Child Rider Annual Premium', type: 'currency'},
    ];

    const healthRating = quote.healthRatingAmeritasTerm.value;
    const ameritas = quote.ameritas.value;
    const lsw = quote.lsw.value;
    const northAmerican = quote.northAmerican.value;
    const tableRatingLSW = quote.tableRatingLSW.value;
    const healthRatingLSW = quote.healthRatingLSW.value;
    const tableRatingNA = quote.tableRatingNA.value;

    let isNATableRating = false;
    if (tableRatingNA != null) {
        isNATableRating = quoteGuards['tableRatingNA'](parameterStore, quote);
    }

    const out = [] as Array<{
        name: string;
        value: any;
        type: 'year' | 'currency' | 'string';
        fieldName?: TemplateDataFields;
    }>;
    resp?.forEach((pr: Record<string, string>) => {
        if (ptype == 'GUL' && pr.product_name != 'Custom Guarantee Gen 9') {
            return;
        }

        if (ptype == 'Term' && ameritas) {
            switch (healthRating) {
                case 'Rapid':
                    if (
                        !(
                            pr.carrier_health_category.startsWith('Rapid Standard') &&
                            pr.product_name.startsWith('FLX Living Benefits - Rapid Term')
                        )
                    ) {
                        return;
                    }
                    break;
                case 'Standard':
                    if (
                        !(
                            pr.carrier_health_category.startsWith('Standard') &&
                            pr.product_name.startsWith('FLX Living Benefits Term')
                        )
                    ) {
                        return;
                    }
                    break;
                default:
                    if (!pr.product_name.startsWith('FLX Living Benefits Term')) {
                        return;
                    }
                    break;
            }
        }

        if (ptype == 'Term' && lsw) {
            if (healthRatingLSW == 'Standard' && tableRatingLSW == 'None') {
                if (!pr.carrier_health_category.startsWith('Standard')) {
                    return;
                }
            } else {
                switch (tableRatingLSW.trim()) {
                    case 'TableA':
                        if (
                            !(
                                pr.carrier_health_category.startsWith('Express') &&
                                pr.carrier_health_category.startsWith('1')
                            )
                        ) {
                            return;
                        }
                        break;
                    case 'TableE':
                        if (
                            !(
                                pr.carrier_health_category.startsWith('Express') &&
                                pr.carrier_health_category.startsWith('2')
                            )
                        ) {
                            return;
                        }
                        break;
                }
            }
        }

        if (ptype == 'Term' && northAmerican && isNATableRating) {
            if (!pr.ixn_health_category.startsWith('Table') && resp.length > 1) {
                return;
            }
        }

        for (let index = 0; index < outParams.length; index += 1) {
            const op = outParams[index];
            const propertyValue = pr[op.id];
            if (propertyValue !== null && propertyValue !== '') {
                switch (op.label) {
                    case 'Face amount':
                        out.push({
                            name: op.label,
                            value: propertyValue,
                            type: op.type,
                            fieldName: TemplateDataFields.faceAmount,
                        });
                        break;
                    case 'Monthly premium':
                        out.push({
                            name: op.label,
                            value: propertyValue,
                            type: op.type,
                            fieldName: TemplateDataFields.monthlyPremium,
                        });
                        break;
                    case 'Product name':
                        out.push({
                            name: op.label,
                            value: propertyValue,
                            type: op.type,
                            fieldName: TemplateDataFields.productName,
                        });
                        break;
                    case 'Carrier':
                        out.push({
                            name: op.label,
                            value: propertyValue,
                            type: op.type,
                            fieldName: TemplateDataFields.carrier,
                        });
                        break;
                    default:
                        if (propertyValue) out.push({name: op.label, value: propertyValue, type: op.type});
                        break;
                }
            }
        }
    });

    let {err, faceAmount, monthlyPremium, productName} = filterQuoteResponse(out);

    return {faceAmount, monthlyPremium, productName, out, error: err, requestDetails: args};
};
